import type { StatsQuery } from './statsApi'

/**
 * What an ad-hoc stats query (a "quick report") can be asked about, and which
 * breakdowns make sense for each.
 *
 * The rules follow the ad server: an advertising object only knows the
 * inventory it ran on (ad units, sites, publishers), and a publishing object
 * only knows the demand it carried (advertisers down to creatives) plus the
 * targeting that matched. Everything is also split by its own hierarchy, team
 * and tier, and by time.
 */

export type QuerySide = 'advertising' | 'publishing'

export type QueryObjectType =
  | 'advertiser'
  | 'order'
  | 'lineItem'
  | 'creative'
  | 'earningsAccount'
  | 'site'
  | 'adUnit'

export type TimeBreakdown = 'MONTHLY' | 'WEEKLY' | 'DAILY' | 'HOURLY'

export type ObjectBreakdown =
  | 'ADVERTISER'
  | 'ORDER'
  | 'LINE_ITEM'
  | 'CREATIVE'
  | 'EARNINGS_ACCOUNT'
  | 'SITE'
  | 'AD_UNIT'
  | 'TEAM'
  | 'TIER'
  | 'MARKETPLACE_PRODUCT'

/** Delivery per targeting value that matched — answered by its own endpoint. */
export const TARGETING_BREAKDOWN = 'TARGETING'

export type Breakdown = TimeBreakdown | ObjectBreakdown | typeof TARGETING_BREAKDOWN

export const TIME_BREAKDOWNS: TimeBreakdown[] = ['MONTHLY', 'WEEKLY', 'DAILY', 'HOURLY']

export function isTimeBreakdown(breakdown: Breakdown | null | undefined): breakdown is TimeBreakdown {
  return !!breakdown && (TIME_BREAKDOWNS as string[]).includes(breakdown)
}

interface ObjectTypeConfig {
  side: QuerySide
  /** Its own breakdown — one row per object. */
  breakdown: ObjectBreakdown
  /** Parents first, closest first: line item → order → advertiser. */
  ancestors: ObjectBreakdown[]
  /** Breakdowns from the other side of the impression. */
  across: ObjectBreakdown[]
  /**
   * Types further up that can pick the objects too, closest first: "every
   * line item of these orders" rather than naming each line item.
   */
  parents: QueryObjectType[]
  /** Type name the search endpoint uses. */
  searchType: string
}

const INVENTORY: ObjectBreakdown[] = ['AD_UNIT', 'SITE', 'EARNINGS_ACCOUNT']
const DEMAND: ObjectBreakdown[] = ['ADVERTISER', 'ORDER', 'LINE_ITEM', 'CREATIVE']

export const QUERY_OBJECT_TYPES: Record<QueryObjectType, ObjectTypeConfig> = {
  advertiser: {
    side: 'advertising',
    breakdown: 'ADVERTISER',
    ancestors: [],
    across: INVENTORY,
    parents: [],
    searchType: 'Advertiser',
  },
  order: {
    side: 'advertising',
    breakdown: 'ORDER',
    ancestors: ['ADVERTISER'],
    across: INVENTORY,
    parents: ['advertiser'],
    searchType: 'Order',
  },
  lineItem: {
    side: 'advertising',
    breakdown: 'LINE_ITEM',
    ancestors: ['ORDER', 'ADVERTISER'],
    across: INVENTORY,
    parents: ['order', 'advertiser'],
    searchType: 'LineItem',
  },
  creative: {
    side: 'advertising',
    breakdown: 'CREATIVE',
    ancestors: ['LINE_ITEM', 'ORDER'],
    // A creative is reached through its line item, so the advertiser is one
    // more step up than its ancestors reach.
    across: ['ADVERTISER', ...INVENTORY],
    parents: ['lineItem', 'order', 'advertiser'],
    searchType: 'Creative',
  },
  earningsAccount: {
    side: 'publishing',
    breakdown: 'EARNINGS_ACCOUNT',
    ancestors: [],
    across: DEMAND,
    parents: [],
    searchType: 'EarningsAccount',
  },
  site: {
    side: 'publishing',
    breakdown: 'SITE',
    ancestors: ['EARNINGS_ACCOUNT'],
    across: DEMAND,
    parents: ['earningsAccount'],
    searchType: 'Site',
  },
  adUnit: {
    side: 'publishing',
    breakdown: 'AD_UNIT',
    ancestors: ['SITE', 'EARNINGS_ACCOUNT'],
    across: DEMAND,
    parents: ['site', 'earningsAccount'],
    searchType: 'AdUnit',
  },
}

export const OBJECT_TYPES_BY_SIDE: Record<QuerySide, QueryObjectType[]> = {
  advertising: ['advertiser', 'order', 'lineItem', 'creative'],
  publishing: ['earningsAccount', 'site', 'adUnit'],
}

/** The object type each breakdown names, for reading a chunk's entity back. */
export const BREAKDOWN_ENTITY_KEY: Record<ObjectBreakdown, string> = {
  ADVERTISER: 'advertiser',
  ORDER: 'order',
  LINE_ITEM: 'lineItem',
  CREATIVE: 'creative',
  EARNINGS_ACCOUNT: 'earningsAccount',
  SITE: 'site',
  AD_UNIT: 'adUnit',
  TEAM: 'team',
  TIER: 'tier',
  MARKETPLACE_PRODUCT: 'axProduct',
}

/** The object type a breakdown refers to, if it is one of the queryable ones. */
export function objectTypeOf(breakdown: Breakdown): QueryObjectType | null {
  const entry = Object.entries(QUERY_OBJECT_TYPES).find(([, c]) => c.breakdown === breakdown)
  return entry ? (entry[0] as QueryObjectType) : null
}

export interface BreakdownOptions {
  time: TimeBreakdown[]
  /** The object's own hierarchy, plus team, tier and marketplace product. */
  own: ObjectBreakdown[]
  /** The other side of the impression. */
  across: (ObjectBreakdown | typeof TARGETING_BREAKDOWN)[]
}

/** Every first-level breakdown the object type allows. */
export function breakdownOptions(type: QueryObjectType, marketplace: boolean): BreakdownOptions {
  const config = QUERY_OBJECT_TYPES[type]
  return {
    time: TIME_BREAKDOWNS,
    own: [
      config.breakdown,
      ...config.ancestors,
      'TEAM',
      ...(marketplace ? (['MARKETPLACE_PRODUCT'] as const) : []),
      'TIER',
    ],
    across: config.side === 'publishing' ? [...config.across, TARGETING_BREAKDOWN] : config.across,
  }
}

/** What a first-level breakdown can be split into. */
export type SubBreakdown = ObjectBreakdown

/**
 * What each row of a first-level breakdown can be split into again. Splitting
 * only goes one way: from the breakdown down into the object's own hierarchy
 * and team. Time is only ever the first level, and one row per object is
 * already as fine as it gets, so neither of those has anything under it.
 * Targeting has no second level either.
 */
export function subBreakdownOptions(
  type: QueryObjectType,
  breakdown: Breakdown,
  marketplace: boolean
): SubBreakdown[] {
  const config = QUERY_OBJECT_TYPES[type]
  if (breakdown === TARGETING_BREAKDOWN || breakdown === config.breakdown) return []
  const hierarchy: ObjectBreakdown[] = [
    config.breakdown,
    ...config.ancestors,
    'TEAM',
    ...(marketplace ? (['MARKETPLACE_PRODUCT'] as const) : []),
  ]
  return hierarchy.filter((o) => o !== breakdown)
}

/** Political ads filter — the political workflow cares about this most of all. */
export type PoliticalFilter = 'all' | 'only' | 'exclude'

export interface QueryDefinition {
  objectType: QueryObjectType
  breakdown: Breakdown
  subBreakdown: SubBreakdown | null
  /** Specific objects to report on. */
  objectIds: string[]
  /**
   * Parents whose objects are reported on as well, e.g. every order of these
   * advertisers. With nothing here or in `objectIds`, every object counts.
   */
  parentIds: Partial<Record<QueryObjectType, string[]>>
  startDate: string
  endDate: string
  currency: string
  political: PoliticalFilter
  /** Also list objects that delivered nothing in the period. */
  includeAll: boolean
}

/**
 * Turns a query into the stats request. Each part maps straight onto the API:
 * the breakdown is `groupBy`, the split under it is `aggregateTo`, and the
 * objects are either named by id or taken `all` at once. The ad server adds
 * up every id it is given — the objects named plus those under each parent —
 * so the pickers combine as "and also", never narrowing each other.
 */
export function toStatsParams(query: QueryDefinition): StatsQuery {
  const config = QUERY_OBJECT_TYPES[query.objectType]
  const params: StatsQuery = {
    startDate: query.startDate,
    endDate: query.endDate,
    currency: query.currency,
    includeAll: query.includeAll,
    includeChunkRange: true,
    timeout: 180000,
    // The advertising side counts the traffic it could have bid on only when asked.
    showFilteredBidReasons: config.side === 'advertising' ? true : undefined,
  }
  if (query.breakdown !== TARGETING_BREAKDOWN) params.groupBy = query.breakdown
  if (query.subBreakdown) params.aggregateTo = query.subBreakdown
  const picked: [QueryObjectType, string[]][] = [
    [query.objectType, query.objectIds],
    ...config.parents.map((type): [QueryObjectType, string[]] => [type, query.parentIds[type] ?? []]),
  ]
  const named = picked.filter(([, ids]) => ids.length > 0)
  // `all` is only for when nothing is named: sent alongside ids that match
  // nothing, it would quietly widen the report to the whole network.
  if (named.length) named.forEach(([type, ids]) => (params[ID_PARAM[type]] = ids.join(';')))
  else params.all = query.objectType
  if (query.political !== 'all') params.political = query.political === 'only'
  return params
}

/** The request parameter naming specific objects of each type. */
const ID_PARAM = {
  advertiser: 'advertiserId',
  order: 'orderId',
  lineItem: 'lineItemId',
  creative: 'creativeId',
  earningsAccount: 'earningsAccountId',
  site: 'siteId',
  adUnit: 'adUnitId',
} as const satisfies Record<QueryObjectType, keyof StatsQuery>
